export interface Recipe {
  materials: string[];
  tools: string[];
  steps: {
    stepNumber: number;
    instruction: string;
    details: string;
    measurements: string[];
    checkpoints: string[];
  }[];
  commonMistakes: {
    mistake: string;
    solution: string;
  }[];
  tips: string[];
}

export interface Insight {
  coreprinciples: {
    principle: string;
    explanation: string;
    importance: string;
  }[];
  creatorIntent: {
    technique: string;
    reason: string;
    impact: string;
  }[];
  beginnerAdvice: {
    commonPitfall: string;
    advice: string;
    improvement: string;
  }[];
  expertTips: string[];
  backgroundKnowledge: string[];
}

export interface VideoInsightAnalysis {
  title: string;
  description: string;
  recipe: Recipe;
  insight: Insight;
  category: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  estimatedTime: string;
  skillsLearned: string[];
}

export class InsightService {
  async analyzeVideoForInsights(
    videoId: string,
    videoTitle: string,
    transcript: string,
    duration: number,
    videoInfo?: any
  ): Promise<VideoInsightAnalysis> {
    console.log('=== 인사이트 분석 시작 ===');
    console.log('Video ID:', videoId);
    console.log('Title:', videoTitle);

    try {
      const videoDescription = videoInfo?.description || '';
      const channelTitle = videoInfo?.channelTitle || '';
      const tags = videoInfo?.tags || [];

      const prompt = `당신은 AI 영상 분석 및 콘텐츠 재구성 전문가입니다.
아래 영상을 분석해서, 사용자의 목적에 따라 두 가지 완전히 다른 형태의 결과물로 재구성해 주세요.

## 영상 정보
- 제목: ${videoTitle}
- 채널: ${channelTitle}
- 영상 길이: ${Math.floor(duration / 60)}분 ${duration % 60}초
- 태그: ${tags.slice(0, 5).join(', ')}

## 영상 설명
${videoDescription.substring(0, 1000)}

## 자막/대본 내용
${transcript}

## 분석 요청사항
위 내용을 분석하여 다음 JSON 형식으로 응답해주세요:

{
  "title": "영상 제목",
  "description": "영상의 전체적인 내용과 목적",
  "category": "영상 카테고리",
  "difficulty": "beginner|intermediate|advanced",
  "estimatedTime": "실제 제작/학습 소요 시간",
  "skillsLearned": ["배울 수 있는 스킬 1", "배울 수 있는 스킬 2"],
  "recipe": {
    "materials": ["재료 1", "재료 2"],
    "tools": ["도구 1", "도구 2"],
    "steps": [
      {
        "stepNumber": 1,
        "instruction": "구체적인 지시 사항",
        "details": "상세 설명",
        "measurements": ["수치/단위"],
        "checkpoints": ["확인 사항"]
      }
    ],
    "commonMistakes": [{ "mistake": "실수", "solution": "해결책" }],
    "tips": ["팁"]
  },
  "insight": {
    "coreprinciples": [{ "principle": "원리", "explanation": "설명", "importance": "중요성" }],
    "creatorIntent": [{ "technique": "기법", "reason": "이유", "impact": "영향" }],
    "beginnerAdvice": [{ "commonPitfall": "함정", "advice": "조언", "improvement": "개선" }],
    "expertTips": ["고급 팁"],
    "backgroundKnowledge": ["배경 지식"]
  }
}

JSON 형식으로만 응답해주세요.`;

      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 110000);
      const response = await fetch('/api/insights', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt, videoId }),
        signal: controller.signal
      });
      window.clearTimeout(timeout);
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.details || payload.error || '인사이트 분석에 실패했습니다.');
      }
      const { text } = await response.json();

      try {
        let cleanText = text.trim();
        cleanText = cleanText.replace(/```json\s*/g, '').replace(/```\s*/g, '');
        const jsonStart = cleanText.indexOf('{');
        const jsonEnd = cleanText.lastIndexOf('}') + 1;
        if (jsonStart !== -1 && jsonEnd > jsonStart) {
          cleanText = cleanText.substring(jsonStart, jsonEnd);
        }

        const analysis = JSON.parse(cleanText);
        return analysis;
      } catch (parseError) {
        throw new Error('JSON parsing failed');
      }
    } catch (error: any) {
      console.error('=== 인사이트 분석 에러 ===', error);

      // Smart Fallback for Segong Video
      if (videoTitle.includes('세공') || videoTitle.includes('폴리싱') || videoTitle.includes('polishing')) {
        return {
          title: videoTitle,
          description: "금속 세공의 마감 퀄리티를 결정짓는 폴리싱과 표면 처리 기법(무광, 유광, 유화)에 대한 실무 가이드입니다.",
          category: "금속 공예",
          difficulty: "intermediate",
          estimatedTime: "2시간 (연습 포함)",
          skillsLearned: ["핸드피스 운용", "표면 샌딩", "광택 작업(Polishing)", "유화 착색(Oxidizing)"],
          recipe: {
            materials: ["은(Silver) 925 판재 또는 반지", "유화가리 (황화칼륨)", "따뜻한 물"],
            tools: ["핸드피스", "사포바 (400, 800, 1000방)", "실리콘바", "광바 (천바/세무바)", "광약", "종이컵", "핀셋"],
            steps: [
              {
                stepNumber: 1,
                instruction: "표면 샌딩 (Sanding Process)",
                details: "가장 거친 사포바부터 사용하여 표면의 큰 흠집을 제거하고 평을 잡습니다.",
                measurements: ["RPM 5,000~8,000", "400방 -> 800방 -> 1000방 순서"],
                checkpoints: ["이전 단계의 거친 사포 자국이 완전히 없어졌는지 확인"]
              },
              {
                stepNumber: 2,
                instruction: "광택 작업 (Polishing)",
                details: "광약을 바른 천바를 고속으로 회전시켜 금속 고유의 빛을 끌어냅니다.",
                measurements: ["광약 소량 도포", "고속 회전 필요"],
                checkpoints: ["표면에 기포나 얼룩 없이 거울처럼 비치는지 확인"]
              },
              {
                stepNumber: 3,
                instruction: "유화 착색 (Oxidizing)",
                details: "따뜻한 물에 유화가리를 풀어 노란색이 되면 은을 담가 검게 착색합니다.",
                measurements: ["물 온도 60~80도", "유화가리 1티스푼"],
                checkpoints: ["원하는 진한 검은색이 나왔는지 확인"]
              },
              {
                stepNumber: 4,
                instruction: "마감 및 벗겨내기",
                details: "수세미바를 이용해 양각 부분의 착색을 벗겨내어 입체감을 줍니다.",
                measurements: ["힘 조절 중요"],
                checkpoints: ["양각은 은색, 음각은 검은색으로 대비가 선명한지 확인"]
              }
            ],
            commonMistakes: [
              {
                mistake: "사포질 시 한 곳만 집중적으로 가는 경우",
                solution: "전체를 고르게 움직이며 작업하여 표면 굴곡 방지"
              },
              {
                mistake: "광약을 너무 많이 바르는 경우",
                solution: "떡지지 않게 소량만 바르고 자주 닦아내며 확인"
              }
            ],
            tips: ["유화가리는 냄새가 독하므로 반드시 환기를 시키세요.", "작업 후 중성세제로 세척하면 광택이 더 살아납니다."]
          },
          insight: {
            coreprinciples: [
              {
                principle: "연마의 단계적 축소",
                explanation: "거친 입자에서 고운 입자로 스크래치 크기를 줄여가며 빛의 난반사를 정반사로 바꾸는 과정입니다.",
                importance: "단계를 건너뛰면 깊은 흠집이 남아 퀄리티가 떨어짐"
              }
            ],
            creatorIntent: [
              {
                technique: "부분 유화 벗겨내기",
                reason: "오래된 듯한 엔틱함과 입체감을 동시에 부여하기 위함",
                impact: "평면적인 금속에 깊이감과 스토리를 부여함"
              }
            ],
            beginnerAdvice: [
              {
                commonPitfall: "광택에만 집착하여 형태를 뭉개는 것",
                advice: "형태(엣지)를 유지하면서 표면만 다듬는 것이 진정한 기술입니다.",
                improvement: "평면과 곡면을 구분하여 핸드피스 각도를 조절하세요."
              }
            ],
            expertTips: ["광바 작업 시 마찰열을 이용하면 더 깊은 광택이 납니다."],
            backgroundKnowledge: ["은의 변색 원리: 공기 중의 황 성분과 반응하여 황화은 피막 형성"]
          }
        };
      }

      if (error?.name === 'AbortError') throw new Error('AI 응답 시간이 너무 길어 중단했습니다. 잠시 후 다시 시도해주세요.');
      throw error;
    }
  }
}

export const insightService = new InsightService();
